import DatePicker from "@/components/date-picker";
import { SectionHeader } from "@/components/common/section-components/SectionHeader";
import {
  AsyncAutocompleteSelect,
  Option,
} from "@/components/select/AsyncAutocompleteSelect";
import { SelectComponent } from "@/components/select/Select";
import { CustomCreatableSelect } from "@/components/select/CustomCreatableSelect";
import { InputField } from "@/components/input-field/InputField";
import { TextArea } from "@/components/text-area/TextArea";
import { AutocompleteSelect } from "@/components/select/AutocompleteSelect";
import { InputCurrency } from "@/components/input-currency/InputCurrency";
import { toaster } from "@/components/ui/toaster";
import { AppRoutes } from "@/constants/routes";
import { AppColors } from "@/styles/colors";
import { Card, Field, Flex, Grid, GridItem, Text } from "@chakra-ui/react";
import { zodResolver } from "@hookform/resolvers/zod";
import dayjs from "dayjs";
import { useEffect, useMemo, useRef, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { CategoriaServicio, ModalidadDosisServicio } from "@/constants/enums";
import { loadOptionsHandler } from "@/pages/grain-requests/sell-orders/schema/handler-functions";
import { getUsuariosMobileByCuenta } from "@/services/users.service";
import { getProcedencias, ProcedenciaEntity } from "@/services/aux-entities.service";
import {
  CatalogoServicioItem,
  getCatalogoServicio,
} from "@/services/service-requests.service";
import { getFertilizantes } from "@/services/products.service";
import { getProductionGrainsList } from "@/services/production-grains.service";
import { getVoleoRequestPrice } from "@/services/voleo-requests.service";
import { getCuentasByBusqueda, getSucursales, Sucursal } from "@/services/accounts.service";
import { onlyNumbers } from "@/utils/reg-expressions";
import { handleTonChangeInInput, isEmpty } from "@/utils/query-string";
import { useRoles } from "@/hooks/useRoles";
import { useDebounce } from "@/hooks/useDebounce";
import { voleoRequestCreationSchema } from "../schema/voleo-request-creation.schema";
import { createVoleoRequestHandler } from "../schema/handler-functions";
import { AttachPrescriptionModal } from "./AttachPrescriptionModal";

type SelectItem = { label: string; value: string };

const modalidadItems: SelectItem[] = [
  { label: "Fija", value: String(ModalidadDosisServicio.Fija) },
  { label: "Variable", value: String(ModalidadDosisServicio.Variable) },
];

export const VoleoRequestCreationPage = () => {
  const navigate = useNavigate();
  const { fetchPermissions } = useRoles();

  const [isActive, setIsActive] = useState(false);
  const [loadingCreate, setLoadingCreate] = useState(false);

  const {
    register,
    control,
    formState: { errors },
    setValue,
    setError,
    clearErrors,
  } = useForm({
    resolver: zodResolver(voleoRequestCreationSchema),
    defaultValues: {
      sucursal: [],
      modalidad: [],
      tipoServicio: [],
      tipoCultivo: [],
      producto: "",
      hectareas: "",
      dosis: "",
      cantidadTotalCalculada: "",
      valorUnitario: "",
      valorTotal: "",
      telefonoContacto: "",
      observaciones: "",
    },
  });

  const watchedValues = useWatch({ control });
  const watchedAccount = useWatch({ control, name: "account" });
  const watchedModalidad = useWatch({ control, name: "modalidad" });
  const watchedTipoServicio = useWatch({ control, name: "tipoServicio" });
  const watchedHectareas = useWatch({ control, name: "hectareas" });
  const watchedDosis = useWatch({ control, name: "dosis" });

  const handleCancel = () => {
    navigate(AppRoutes.VOLEO_REQUESTS);
  };

  const [openAttachModal, setOpenAttachModal] = useState(false);

  const handleAdd = () => {
    setOpenAttachModal(true);
  };

  const handleConfirmAttachAndSubmit = async (file?: File) => {
    await createVoleoRequestHandler({
      values: watchedValues,
      file,
      productos: productos.map((p: any) => ({ id: Number(p?.id), nombre: String(p?.nombre ?? "") })),
      setLoadingCreate,
      navigate,
      fetchPermissions,
    });
  };

  const [loadingAccounts, setLoadingAccounts] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const getAccountOptions = async (inputValue: string) => {
    const filtered = await getCuentasByBusqueda({ busqueda: inputValue });
    return (
      filtered?.map((p) => ({
        label: `${p.titular} - ${p.nroCuenta} - ${p.tipoCuenta}`,
        value: p.nroCuenta,
        tipoCuenta: p.tipoCuenta,
        idTipoCuenta: p.idTipoCuenta,
        zonaComercial: p.zonaComercial,
      })) ?? []
    );
  };

  const loadAccountOptions = (inputValue: string): Promise<Option[]> => {
    return loadOptionsHandler({
      getOptions: getAccountOptions as any,
      inputValue,
      setLoadingPersons: setLoadingAccounts,
      timeoutRef,
    });
  };

  const [loadingSucursales, setLoadingSucursales] = useState(false);
  const [sucursales, setSucursales] = useState<Sucursal[] | null>([]);

  const fetchSucursales = async () => {
    setLoadingSucursales(true);
    const response = await getSucursales();
    setSucursales(response);
    setLoadingSucursales(false);
  };

  const [cuentaUsuarios, setCuentaUsuarios] = useState<any[]>([]);
  const [loadingCuentaUsuarios, setLoadingCuentaUsuarios] = useState(false);

  const fetchUsuariosByCuenta = async (cuenta?: string) => {
    if (!cuenta) return;
    setLoadingCuentaUsuarios(true);
    const response = await getUsuariosMobileByCuenta({ cuenta });
    setCuentaUsuarios(response ?? []);
    setLoadingCuentaUsuarios(false);
  };

  const [procedencias, setProcedencias] = useState<ProcedenciaEntity[]>([]);
  const [loadingProcedencias, setLoadingProcedencias] = useState(false);

  const fetchProcedencias = async (cuenta?: string) => {
    if (!cuenta) return;
    const nro = Number(cuenta);
    if (!Number.isFinite(nro)) return;

    setLoadingProcedencias(true);
    const response = await getProcedencias({ cuenta: nro });
    setProcedencias(response ?? []);
    setLoadingProcedencias(false);
  };

  // El id de establecimiento que espera el backend es el idInterno (numérico),
  // no el id (string) usado por otras entidades auxiliares.
  const procedenciaItems: SelectItem[] = useMemo(() => {
    return (
      procedencias?.map((p) => ({
        label: p?.nombre ?? "-",
        value: String(p?.idInterno ?? ""),
      })) ?? []
    );
  }, [procedencias]);

  const [loadingCatalogo, setLoadingCatalogo] = useState(false);
  const [catalogo, setCatalogo] = useState<CatalogoServicioItem[]>([]);

  const fetchCatalogo = async () => {
    setLoadingCatalogo(true);
    try {
      const response = await getCatalogoServicio({
        Categoria: CategoriaServicio.Voleo,
      });
      setCatalogo(response ?? []);
    } finally {
      setLoadingCatalogo(false);
    }
  };

  const tipoServicioItems: SelectItem[] = useMemo(() => {
    return (
      catalogo?.map((s: any) => ({
        label: String(s?.nombre ?? "-"),
        value: String(s?.id ?? ""),
      })) ?? []
    );
  }, [catalogo]);

  const [loadingCultivos, setLoadingCultivos] = useState(false);
  const [cultivos, setCultivos] = useState<any[]>([]);

  const fetchCultivos = async () => {
    setLoadingCultivos(true);
    try {
      const response = await getProductionGrainsList({
        "P.Pagina": 1,
        "P.Records": 100,
      });
      setCultivos(response?.listado ?? []);
    } finally {
      setLoadingCultivos(false);
    }
  };

  const tipoCultivoItems: SelectItem[] = useMemo(() => {
    return (
      cultivos?.map((c: any) => ({
        label: String(c?.nombre ?? "-"),
        value: String(c?.id ?? ""),
      })) ?? []
    );
  }, [cultivos]);

  const [loadingProductos, setLoadingProductos] = useState(false);
  const [productos, setProductos] = useState<any[]>([]);

  const fetchProductos = async () => {
    setLoadingProductos(true);
    try {
      const response = await getFertilizantes({ activo: true });
      setProductos(response ?? []);
    } finally {
      setLoadingProductos(false);
    }
  };

  const productoItems: SelectItem[] = useMemo(() => {
    return (
      productos?.map((p: any) => ({
        label: String(p?.nombre ?? "-"),
        value: String(p?.id ?? ""),
      })) ?? []
    );
  }, [productos]);

  const hectareasValue = Number(String(watchedHectareas ?? "").replace(",", ".")) || 0;
  const dosisValue = Number(String(watchedDosis ?? "").replace(",", ".")) || 0;

  useEffect(() => {
    clearErrors("dosis");

    if (isEmpty(watchedDosis)) return;

    if (dosisValue <= 0) {
      setError("dosis", {
        type: "manual",
        message: "La dosis debe ser mayor a 0.",
      });
    }
  }, [watchedDosis, dosisValue, clearErrors, setError]);

  const [loadingPrecio, setLoadingPrecio] = useState(false);
  const [valorUnitario, setValorUnitario] = useState(0);

  const fetchPrecio = async (tipoServicio: number, modalidad: number, dosis: number) => {
    setLoadingPrecio(true);
    try {
      const response = await getVoleoRequestPrice({
        TipoServicio: tipoServicio,
        Modalidad: modalidad,
        DosisKgHa: dosis,
      });
      setValorUnitario(Number(response?.valorUnitario ?? 0));
    } catch (error: any) {
      setValorUnitario(0);
      if (error?.response?.status === 422) {
        toaster.create({
          type: "warning",
          title:
            error?.response?.data?.message ??
            "No se encontró un precio configurado para la combinación de modalidad y dosis ingresada.",
        });
      } else {
        toaster.create({
          type: "error",
          title: "No se pudo obtener el valor unitario.",
        });
      }
    } finally {
      setLoadingPrecio(false);
    }
  };

  // Espera a que la Dosis termine de tipearse (debounce) antes de considerar
  // completos Tipo de servicio + Modalidad + Dosis, y solo pide el precio de
  // nuevo si cambia alguno de los tres valores ya asentados.
  const debouncedDosis = useDebounce(dosisValue, 600);
  const lastPricedKeyRef = useRef<string | null>(null);

  useEffect(() => {
    const modalidadId = Number(
      Array.isArray(watchedModalidad) ? watchedModalidad?.[0] : watchedModalidad,
    );
    const tipoServicioId = Number(
      Array.isArray(watchedTipoServicio) ? watchedTipoServicio?.[0] : watchedTipoServicio,
    );

    const isComplete =
      Number.isFinite(modalidadId) && Number.isFinite(tipoServicioId) && debouncedDosis > 0;

    if (!isComplete) {
      lastPricedKeyRef.current = null;
      setValorUnitario(0);
      return;
    }

    const key = `${tipoServicioId}-${modalidadId}-${debouncedDosis}`;
    if (lastPricedKeyRef.current === key) return;
    lastPricedKeyRef.current = key;

    void fetchPrecio(tipoServicioId, modalidadId, debouncedDosis);
  }, [watchedModalidad, watchedTipoServicio, debouncedDosis]);

  const valorTotal = Number.isFinite(valorUnitario)
    ? Number((hectareasValue * valorUnitario).toFixed(2))
    : 0;

  const cantidadTotalCalculada = Number((dosisValue * hectareasValue).toFixed(2));

  useEffect(() => {
    setValue("valorUnitario", valorUnitario ? String(valorUnitario) : "");
  }, [valorUnitario, setValue]);

  useEffect(() => {
    setValue("valorTotal", valorTotal ? String(valorTotal) : "");
  }, [valorTotal, setValue]);

  useEffect(() => {
    setValue(
      "cantidadTotalCalculada",
      cantidadTotalCalculada
        ? `${new Intl.NumberFormat("es-AR").format(cantidadTotalCalculada)} kg`
        : "",
    );
  }, [cantidadTotalCalculada, setValue]);

  const hasNoFilledValues = useMemo(() => {
    const {
      account,
      procedencia,
      modalidad,
      tipoServicio,
      tipoCultivo,
      producto,
      telefonoContacto,
      fechaDeseada,
    } = watchedValues || {};

    return (
      isEmpty(account) ||
      isEmpty(procedencia) ||
      isEmpty(modalidad) ||
      isEmpty(tipoServicio) ||
      isEmpty(tipoCultivo) ||
      isEmpty(String(producto ?? "").trim()) ||
      isEmpty(fechaDeseada) ||
      isEmpty(telefonoContacto) ||
      hectareasValue <= 0 ||
      dosisValue <= 0
    );
  }, [watchedValues, hectareasValue, dosisValue]);

  useEffect(() => {
    setIsActive(true);
  }, []);

  useEffect(() => {
    if (isActive) void fetchCatalogo();
  }, [isActive]);

  useEffect(() => {
    if (isActive) void fetchCultivos();
  }, [isActive]);

  useEffect(() => {
    if (isActive) void fetchProductos();
  }, [isActive]);

  useEffect(() => {
    if (isActive) void fetchSucursales();
  }, [isActive]);

  useEffect(() => {
    if (!watchedAccount?.value) return;
    setValue("usuarioMobile", []);
    setValue("procedencia", []);
    setValue("sucursal", []);
    setProcedencias([]);
    void fetchUsuariosByCuenta(String(watchedAccount.value));
    void fetchProcedencias(String(watchedAccount.value));

    const claveSucursal = String(watchedAccount?.zonaComercial ?? "");
    if (claveSucursal) {
      setValue("sucursal", [claveSucursal]);
    }
  }, [watchedAccount, setValue]);

  return (
    <Flex flexDir="column" paddingTop="20px" gap="12px">
      <SectionHeader
        title="Agregar solicitud de voleo"
        addButtonLabel="Agregar"
        cancelButtonLabel="Cancelar"
        addButtonType="button"
        handleAdd={handleAdd}
        handleCancel={handleCancel}
        disableAdd={hasNoFilledValues || loadingCreate || loadingPrecio}
        loading={loadingCreate}
      />

      <Card.Root maxW="full" marginTop="30px" borderRadius="8px">
        <Card.Body
          paddingX="48px"
          paddingY="30px"
          borderRadius="8px"
          border={`1px solid ${AppColors.WHITE_BORDER_1}`}
        >
          <Grid templateColumns="repeat(4, 1fr)" gap={6} alignItems="end">
            <GridItem colSpan={1} paddingY="8px">
              <Field.Root invalid={!!errors?.account?.message} gap="12px">
                <Field.Label>
                  <Text fontSize="16px" fontWeight={600}>Cuenta</Text>
                  <Text fontSize="16px" fontWeight={600} color={AppColors.REQUIRED}>
                    *
                  </Text>
                </Field.Label>
                <AsyncAutocompleteSelect
                  name="account"
                  control={control}
                  loadOptions={loadAccountOptions}
                  placeholder="Seleccionar"
                  isLoading={loadingAccounts}
                  disabled={loadingCreate}
                  triggerWithLength={false}
                />
              </Field.Root>
            </GridItem>

            <GridItem colSpan={1} paddingY="8px">
              <SelectComponent
                fullWidth
                name="sucursal"
                control={control}
                labelText="Sucursal"
                placeholder="-"
                items={
                  sucursales?.map((s) => ({
                    label: s?.nombre,
                    value: s?.clave,
                  })) ?? []
                }
                loading={loadingSucursales}
                disabled
                closeOnSelect
                clearable={false}
                size="xs"
                minHeight="40px"
              />
            </GridItem>

            <GridItem colSpan={1} paddingY="8px">
              <Field.Root gap="12px">
                <Field.Label>
                  <Text fontSize="16px" fontWeight={600}>Usuario Mobile</Text>
                </Field.Label>
                <AutocompleteSelect
                  control={control}
                  inputName="usuarioMobile"
                  loading={loadingCuentaUsuarios}
                  disabled={!watchedAccount || loadingCuentaUsuarios || loadingCreate}
                  multiple={false}
                  items={
                    cuentaUsuarios?.map((cu: any) => ({
                      label: `${cu?.nombre} ${cu?.apellido}`,
                      value: cu?.id,
                    })) ?? []
                  }
                />
              </Field.Root>
            </GridItem>

            <GridItem colSpan={1} paddingY="8px">
              <SelectComponent
                fullWidth
                name="procedencia"
                control={control}
                labelText="Establecimiento"
                placeholder="Seleccionar"
                items={procedenciaItems}
                loading={loadingProcedencias}
                disabled={!watchedAccount || loadingProcedencias || loadingCreate}
                closeOnSelect
                clearable
                size="xs"
                minHeight="40px"
                required
              />
            </GridItem>

            <GridItem colSpan={1} paddingY="8px">
              <InputField
                inputName="hectareas"
                labelText="Hectáreas"
                placeholder="Ingresar"
                control={control}
                type="text"
                allowedPattern={onlyNumbers}
                onChange={handleTonChangeInInput}
                minHeight="40px"
                disabled={loadingCreate}
                required
              />
            </GridItem>

            <GridItem colSpan={1} paddingY="8px">
              <SelectComponent
                fullWidth
                name="modalidad"
                control={control}
                labelText="Modalidad"
                placeholder="Seleccionar"
                items={modalidadItems}
                closeOnSelect
                clearable
                size="xs"
                minHeight="40px"
                disabled={loadingCreate}
                required
              />
            </GridItem>

            <GridItem colSpan={1} paddingY="8px">
              <SelectComponent
                fullWidth
                name="tipoServicio"
                control={control}
                labelText="Tipo de servicio"
                placeholder="Seleccionar"
                items={tipoServicioItems}
                loading={loadingCatalogo}
                disabled={loadingCatalogo || loadingCreate}
                closeOnSelect
                clearable
                size="xs"
                minHeight="40px"
                required
              />
            </GridItem>

            <GridItem colSpan={1} paddingY="8px">
              <SelectComponent
                fullWidth
                name="tipoCultivo"
                control={control}
                labelText="Tipo de cultivo"
                placeholder="Seleccionar"
                items={tipoCultivoItems}
                loading={loadingCultivos}
                disabled={loadingCultivos || loadingCreate}
                closeOnSelect
                clearable
                size="xs"
                minHeight="40px"
                required
              />
            </GridItem>

            <GridItem colSpan={1} paddingY="8px">
              <CustomCreatableSelect
                control={control}
                name="producto"
                labelText="Producto"
                placeholder="Seleccionar o escribir"
                items={productoItems}
                loading={loadingProductos}
                disabled={loadingProductos || loadingCreate}
                required
              />
            </GridItem>

            <GridItem colSpan={1} paddingY="8px">
              <InputField
                inputName="dosis"
                labelText="Dosis (kg/ha)"
                placeholder="Ingresar"
                control={control}
                type="text"
                allowedPattern={onlyNumbers}
                onChange={handleTonChangeInInput}
                minHeight="40px"
                disabled={loadingCreate}
                required
                floatingError
              />
            </GridItem>

            <GridItem colSpan={1} paddingY="8px">
              <InputField
                inputName="cantidadTotalCalculada"
                labelText="Cantidad total calculada"
                placeholder="-"
                control={control}
                type="text"
                minHeight="40px"
                disabled
              />
            </GridItem>

            <GridItem colSpan={1} paddingY="8px">
              <Field.Root gap="12px">
                <Field.Label>
                  <Text fontSize="16px" fontWeight={600}>Valor Unitario (USD/ha)</Text>
                </Field.Label>
                <InputCurrency
                  control={control}
                  name="valorUnitario"
                  height="40px"
                  maxLength={20}
                  disabled
                />
              </Field.Root>
            </GridItem>

            <GridItem colSpan={1} paddingY="8px">
              <Field.Root gap="12px">
                <Field.Label>
                  <Text fontSize="16px" fontWeight={600}>Valor Total Servicio (USD)</Text>
                </Field.Label>
                <InputCurrency
                  control={control}
                  name="valorTotal"
                  height="40px"
                  maxLength={20}
                  disabled
                />
              </Field.Root>
            </GridItem>

            <GridItem colSpan={1} paddingY="8px">
              <InputField
                inputName="telefonoContacto"
                labelText="Teléfono de contacto"
                placeholder="Ingresar"
                control={control}
                type="text"
                minHeight="40px"
                disabled={loadingCreate}
                required
                maxLength={15}
              />
            </GridItem>

            <GridItem colSpan={1} paddingY="8px">
              <DatePicker
                control={control}
                name="fechaDeseada"
                labelText="Fecha deseada"
                placeholder="Seleccionar"
                minDate={dayjs()}
                disablePortal={false}
                minHeight="40px"
                disabled={loadingCreate}
                required
              />
            </GridItem>
          </Grid>
        </Card.Body>
      </Card.Root>

      <Flex flexDir="column" gap="12px">
        <Text fontWeight={700} fontSize="20px">
          Detalle
        </Text>
        <TextArea
          register={register as any}
          errors={errors}
          inputName="observaciones"
          placeholder=""
          maxLength={200}
          helperText="Máximo 200 caracteres"
          disabled={loadingCreate}
        />
      </Flex>

      <AttachPrescriptionModal
        open={openAttachModal}
        setOpen={setOpenAttachModal}
        onAccept={handleConfirmAttachAndSubmit}
      />
    </Flex>
  );
};
